/**
 * Web Push helpers for the CRM.
 *
 * The service worker is registered from index.js (prod-only). In dev the
 * subscribe flow will no-op gracefully with a clear error toast.
 */

import { pushApi } from "./api";

export const PUSH_SUPPORTED =
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

function urlBase64ToUint8Array(base64String) {
  // Standard helper — VAPID public keys are base64url without padding.
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) output[i] = raw.charCodeAt(i);
  return output;
}

async function getRegistration() {
  if (!PUSH_SUPPORTED) return null;
  // In dev the SW isn't registered (index.js skips it). Register on demand so
  // agents testing locally can still enable push.
  try {
    return (
      (await navigator.serviceWorker.getRegistration("/sw.js")) ||
      (await navigator.serviceWorker.register("/sw.js"))
    );
  } catch (e) {
    console.warn("[push] getRegistration failed:", e);
    return null;
  }
}

export async function getPushState() {
  if (!PUSH_SUPPORTED) {
    return { supported: false, permission: "denied", subscribed: false };
  }
  const reg = await getRegistration();
  const sub = reg ? await reg.pushManager.getSubscription() : null;
  return {
    supported: true,
    permission: Notification.permission,
    subscribed: Boolean(sub),
    endpoint: sub ? sub.endpoint : null,
  };
}

export async function enablePush() {
  if (!PUSH_SUPPORTED) {
    throw new Error("Push notifications are not supported in this browser.");
  }
  const reg = await getRegistration();
  if (!reg) throw new Error("Service worker is unavailable.");

  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error(
      permission === "denied"
        ? "Notifications are blocked for this site — enable them in browser settings."
        : "Notifications permission was not granted."
    );
  }

  const { data } = await pushApi.vapidKey();
  if (!data.configured || !data.public_key) {
    throw new Error("Server is missing VAPID keys — contact an admin.");
  }

  // Reuse existing subscription if present; otherwise create one.
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(data.public_key),
    });
  }

  await pushApi.subscribe(sub.toJSON());
  return sub;
}

export async function disablePush() {
  if (!PUSH_SUPPORTED) return false;
  const reg = await getRegistration();
  if (!reg) return false;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return false;
  try {
    await pushApi.unsubscribe(sub.endpoint);
  } catch (e) {
    console.warn("[push] backend unsubscribe failed (continuing):", e);
  }
  await sub.unsubscribe();
  return true;
}

export async function sendTestPush() {
  const { data } = await pushApi.test();
  return data;
}
